import {
  ContactApiService,
  DocumentInfo,
  Engagement,
  EngagementApiService,
  EngagementConsentSettings,
  EngagementDocumentContactMapping,
  EngagementDocumentType,
  EngagementHub,
  EngagementHubStep,
  EngagementJobResponse,
  EngagementMode,
  EngagementPrepaymentResponse,
  EngagementReport,
  EngagementsApiService,
  EngagementStatus,
  EngagementStep,
  EngagementStepStatus,
  EngagementStepType,
  EngagementTask,
  EngagementTaskType,
  isTaskCompleted,
  MinimalEngagementHub,
  NON_INTERACTIVE_ENGAGEMENT_STATUSES,
  PAYMENT_INFO_TASKS,
  PaymentInfoType,
  SignatureRequirementType,
  STEP_TYPE_TO_DOCUMENT_TYPE,
  UploadApiService,
  VALID_ENGAGEMENT_STATUSES
} from '@aiwyn/api';
import { AuthService, AuthStateAdaptorService, SecureLinkAuthorization, SecureLinkEntityType } from '@aiwyn/auth';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyState } from '@aiwyn/config';
import {
  BaseContact,
  blobToBase64,
  bytesToBase64,
  EditableFileResponse,
  hexToBytes,
  ignoreFalsy,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  Logger,
  ResetState,
  setLoadableInState,
  SignatureService,
} from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AppState, ShowEngagementsTab } from '../../../state';
import {
  ENGAGEMENT_STEP_LABEL_MAP,
  EngagementPendingChanges,
  EngagementsCurrentStepRef,
  EngagementsPendingChanges,
  EngagementStepDisplay,
  EngagementStepPendingChanges,
  EngagementStepRef,
  STEP_LABEL_DOCUMENT_TYPE_MAPPING
} from '../models';
import {
  ConfirmConsentDocument,
  ConfirmConsentDocumentMultiContact,
  ConfirmEngagementViewed,
  ConfirmPaymentInfo,
  EngagementAvaliabilityCheck,
  GetEngagementConsentSettings,
  GetEngagementDocument,
  GetEngagementHub,
  GetSelectedEngagementJobs,
  GetSelectedEngagementPrepayment,
  LoadMultipleIdentitySigningData,
  MoveToEngagementStep,
  RecoverFromEngagementCompletionFailure,
  RemoveEngagementStepPendingChanges,
  RemoveSelectedEngagementData,
  SaveConsentDocument,
  SaveConsentDocumentMultiContact,
  SaveEngagementLetter,
  SaveEngagementLetterMultiContact,
  SetActiveSigningContact,
  SetEngagementCurrentStepRef,
  SetEngagementStepPendingChanges,
  SetNonInteractiveEngagement,
  SetSelectedEngagementId,
  SetSelectedEngagementPrepaymentAsFailed,
  SetTableSearchValue,
  UpdateSelectedDocument,
  ViewEngagement
} from './engagements.actions';
import { DEFAULT_ENGAGEMENT_STATE_MODEL, EngagementsStateModel, MultipleIdentitySigningState } from './engagements.model';


@State({
  name: 'engagements',
  defaults: DEFAULT_ENGAGEMENT_STATE_MODEL,
})
@Injectable({
  providedIn: 'root',
})
export class EngagementsState {
  /**
   * Hard kill-switch for the contact-switcher dropdown. When false the dropdown is hidden
   * everywhere it is rendered (in-sidebar for authorized users and secure-link sidebar),
   * but every other piece of multi-identity machinery — per-contact signature cache,
   * contact switching via signature click, warning modals, annotation re-evaluation —
   * keeps working. Flip to true to surface the dropdown again.
   */
  static readonly IS_CONTACT_SELECT_DROPDOWN_VISIBLE = false;

  @Selector()
  static getTableSearchValue({ tableSearchValue }: EngagementsStateModel): string {
    return tableSearchValue;
  }

  @Selector()
  static getSelectedEngagementLoadable({ selectedEngagement }: EngagementsStateModel): Loadable<Engagement> {
    return selectedEngagement;
  }

  @Selector([EngagementsState.getSelectedEngagementLoadable])
  static getSelectedEngagement(selectedEngagement: Loadable<Engagement>): Engagement | undefined {
    return selectedEngagement.data;
  }

  @Selector([EngagementsState.getSelectedEngagementLoadable])
  static getSelectedEngagementStatus({ status }: Loadable<Engagement>): LoadableStatus {
    return status;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static isSelectedEngagementReassignable(engagement: Engagement): boolean {
    return engagement?.canReassign ?? false;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static getSelectedEngagementId(engagement: Engagement): number | undefined {
    return engagement?.id;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static getSelectedEngagementWithDate(engagement: Engagement): Engagement | undefined {
    if (!engagement) {
      return undefined;
    }

    if (!engagement.isStartOnAcceptance) {
      return engagement;
    }

    return {
      ...engagement,
      startDate: engagement.isExecuted ? engagement.executedDate : 'On Acceptance',
    };
  }

  static getSelectedEngagementDocumentLoadable(
    fileId: number
  ): (state: EngagementsStateModel) => Loadable<EditableFileResponse> {
    return createSelector([EngagementsState], (state: EngagementsStateModel) => {
      return state.selectedDocuments[fileId] || { status: LoadableStatus.DEFAULT };
    });
  }

  static getSelectedEngagementDocumentStatus(fileId: number) {
    return createSelector(
      [EngagementsState.getSelectedEngagementDocumentLoadable(fileId)],
      (selectedDocumentLoadable) => selectedDocumentLoadable.status,
    );
  }

  static getSelectedEngagementDocument(fileId: number) {
    return createSelector(
      [EngagementsState.getSelectedEngagementDocumentLoadable(fileId)],
      (selectedDocumentLoadable) => selectedDocumentLoadable.data,
    );
  }

  static isSelectedEngagementDocumentEdited(fileId: number) {
    return createSelector(
      [EngagementsState.getSelectedEngagementDocument(fileId)],
      (selectedDocument) => selectedDocument?.isEdited,
    );
  }

  static getNewlyAddedSignatureIds(fileId: number) {
    return createSelector(
      [EngagementsState.getSelectedEngagementDocument(fileId)],
      (selectedDocument) => selectedDocument?.addedSignatureIds,
    );
  }

  @Selector()
  static getSelectedEngagementJobsLoadable({ selectedEngagementJobs }: EngagementsStateModel): Loadable<EngagementJobResponse[]> {
    return selectedEngagementJobs;
  }

  @Selector([EngagementsState.getSelectedEngagementJobsLoadable])
  static getSelectedEngagementJobsStatus({ status }: Loadable<EngagementJobResponse[]>): LoadableStatus {
    return status ?? LoadableStatus.DEFAULT;
  }

  @Selector([EngagementsState.getSelectedEngagementJobsLoadable])
  static getSelectedEngagementJobs({ data }: Loadable<EngagementJobResponse[]>): EngagementJobResponse[] {
    return data ?? [];
  }

  @Selector([EngagementsState.getSelectedEngagementJobsLoadable])
  static getSelectedEngagementPrepaymentJobs({ data }: Loadable<EngagementJobResponse[]>): EngagementJobResponse[] {
    return data?.filter(({ prepaymentAmount }) => prepaymentAmount !== 0) ?? [];
  }

  @Selector([EngagementsState.getSelectedEngagementJobsLoadable])
  static getSelectedEngagementPrepaymentSubtotal({ data }: Loadable<EngagementJobResponse[]>): number {
    return data?.reduce((subtotal, { prepaymentAmount }) => subtotal + prepaymentAmount, 0) ?? 0;
  }

  @Selector()
  static getSelectedEngagementConsentSettings({ selectedEngagementConsentSettings }: EngagementsStateModel): EngagementConsentSettings | undefined {
    return selectedEngagementConsentSettings;
  }

  @Selector([EngagementsState.getSelectedEngagementPrepaymentSubtotal])
  static isSelectedEngagementPrepaymentRequired(prepaymentSubtotal: number): boolean {
    return prepaymentSubtotal !== 0;
  }

  @Selector()
  static getSelectedEngagementPrepaymentLoadable(
    { selectedEngagementPrepayment }: EngagementsStateModel,
  ): Loadable<EngagementPrepaymentResponse> {
    return selectedEngagementPrepayment;
  }

  @Selector([EngagementsState.getSelectedEngagementPrepaymentLoadable])
  static getSelectedEngagementPrepaymentStatus({ status }: Loadable<EngagementPrepaymentResponse>): LoadableStatus {
    return status ?? LoadableStatus.DEFAULT;
  }

  @Selector([EngagementsState.getSelectedEngagementPrepaymentLoadable])
  static getSelectedEngagementPrepayment({ data }: Loadable<EngagementPrepaymentResponse>): EngagementPrepaymentResponse {
    return data;
  }

  @Selector([EngagementsState.getSelectedEngagementLoadable])
  static getSelectedEngagementReportStatus({ status }: Loadable<Engagement>): LoadableStatus {
    return status;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static getSelectedEngagementSteps(selectedEngagement: Engagement): EngagementStep[] {
    return selectedEngagement?.report?.steps ?? [];
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static getSelectedEngagementVisibleSteps(selectedEngagement: Engagement): EngagementStep[] {
    return (selectedEngagement?.report?.steps ?? []).filter(({ hidden, hasTasksForCurrentUser }) => !hidden && hasTasksForCurrentUser);
  }

  @Selector([AppState.getUserContactIds, EngagementsState.getSelectedEngagementVisibleSteps])
  static getSelectedEngagementVisibleStepsDisplay(currentUserContactIds: number[], visibleSteps: EngagementStep[]): EngagementStepDisplay[] {
    const lastStepIndex = visibleSteps.length - 1;
    const lastStep = visibleSteps[lastStepIndex];
    const isLastStepComplete = lastStep?.status === EngagementStepStatus.COMPLETE;

    return visibleSteps.map((visibleStep, index) => {
      const labelValue = ENGAGEMENT_STEP_LABEL_MAP[visibleStep.type];
      const label = typeof labelValue === 'function' ? labelValue(visibleStep.label) : labelValue;

      let status = visibleStep?.status;

      const isLastStep = lastStepIndex === index;
      const stepHasIncompleteRequiredTasksForCurrentUser = visibleStep.tasks.some(task => !isTaskCompleted(task.status) && currentUserContactIds.includes(task.contactId));

      if (isLastStepComplete && !isLastStep && stepHasIncompleteRequiredTasksForCurrentUser) {
        status = EngagementStepStatus.INVALID;
      }

      return { ...visibleStep, label, status };
    });
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static currentUserHasViewedSelectedEngagement(selectedEngagement: Engagement): boolean {
    return selectedEngagement?.report?.currentUserHasViewedEngagement ?? false;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps])
  static getSelectedEngagementVisibleStepCount(visibleSteps: EngagementStep[]): number {
    return visibleSteps.length;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps, EngagementsState.getSelectedEngagementCurrentStep])
  static getSelectedEngagementVisibleStepIndex(visibleSteps: EngagementStep[], currentStep: EngagementStep): number {
    return visibleSteps.findIndex(({ type }) => type === currentStep?.type) + 1;
  }

  @Selector()
  static getEngagementsCurrentStepRef({ engagementsCurrentStepRef }: EngagementsStateModel): EngagementsCurrentStepRef {
    return engagementsCurrentStepRef ?? {};
  }

  @Selector([EngagementsState.getEngagementsCurrentStepRef, EngagementsState.getSelectedEngagementId])
  static getSelectedEngagementCurrentStepRef(engagementsCurrentStepRef: EngagementsCurrentStepRef, selectedEngagementId: number): EngagementStepRef | undefined {
    return engagementsCurrentStepRef?.[selectedEngagementId];
  }

  @Selector([EngagementsState.getSelectedEngagementSteps, EngagementsState.getSelectedEngagementCurrentStepRef])
  static getSelectedEngagementCurrentStep(selectedEngagementSteps: EngagementStep[], selectedEngagementCurrentStepRef?: EngagementStepRef): EngagementStep {
    if (selectedEngagementCurrentStepRef?.type) {
      const byType = selectedEngagementSteps.filter(({ type }) => type === selectedEngagementCurrentStepRef.type);

      if (selectedEngagementCurrentStepRef.documentId != null) {
        return byType.find(step => step.tasks?.some(({ documentId }) => documentId === selectedEngagementCurrentStepRef.documentId));
      }

      return byType[0];
    }

    const firstIncomplete = selectedEngagementSteps.find(step => step.status != EngagementStepStatus.COMPLETE);
    return firstIncomplete ?? selectedEngagementSteps[0];
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps, EngagementsState.getSelectedEngagementCurrentStep])
  static isSelectedEngagementOnFirstStep(visibleEngagementSteps: EngagementStep[], currentStep: EngagementStep): boolean {
    const { tasks, type } = currentStep ?? {};
    const documentId = tasks?.[0]?.documentId;
    const currentStepIndex = visibleEngagementSteps.findIndex(step => step.type === type && (step.tasks?.some(task => task.documentId === documentId) ?? true));

    return visibleEngagementSteps.length > 0 && currentStepIndex === 0;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps, EngagementsState.getSelectedEngagementCurrentStep])
  static isSelectedEngagementOnLastStep(visibleEngagementSteps: EngagementStep[], currentStep: EngagementStep): boolean {
    const { tasks, type } = currentStep ?? {};
    const documentId = tasks?.[0]?.documentId;
    const currentStepIndex = visibleEngagementSteps.findIndex(step => step.type === type && (step.tasks?.some(task => task.documentId === documentId) ?? true));

    return visibleEngagementSteps.length > 0 && currentStepIndex === visibleEngagementSteps.length - 1;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps])
  static getSelectedEngagementLastIncompleteStep(visibleEngagementSteps: EngagementStep[]): EngagementStep | undefined {
    const firstIncompleteStep = visibleEngagementSteps
      .find(({ status, isReadonly }) => !isReadonly && status !== EngagementStepStatus.COMPLETE);

    return firstIncompleteStep;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleSteps, EngagementsState.getSelectedEngagementCurrentStep])
  static isSelectedEngagementOnLastIncompleteStep(visibleEngagementSteps: EngagementStep[], currentStep: EngagementStep): boolean {
    // current step is already complete, so it can't be the final step
    if (currentStep?.status === EngagementStepStatus.COMPLETE) {
      return false;
    }

    const { tasks, type } = currentStep ?? {};
    const documentId = tasks?.[0]?.documentId;

    const otherIncompleteStepsExist = visibleEngagementSteps
      .filter(step => !(step.type === type && (step.tasks?.some(task => task.documentId === documentId) ?? true)))
      .filter(({ status, isReadonly }) => !isReadonly && status !== EngagementStepStatus.COMPLETE)
      .length > 0;

    return !otherIncompleteStepsExist;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static allSelectedEngagementStepsComplete(selectedEngagement: Engagement): boolean {
    return selectedEngagement?.report?.signatureRequirementsMet && selectedEngagement?.report?.paymentInfoRequirementMet;
  }

  @Selector([EngagementsState.getSelectedEngagementVisibleStepsDisplay])
  static someSelectedEngagementStepsInvalid(visibleEngagementSteps: EngagementStep[]): boolean {
    return visibleEngagementSteps.some(({ status }) => status === EngagementStepStatus.INVALID);
  }

  @Selector([
    AppState.getUserContactIds,
    EngagementsState.getSelectedEngagement,
    EngagementsState.getSelectedEngagementSteps,
  ])
  static isUserSignatoryOnSelectedEngagement(
    currentUserContactIds: number[],
    selectedEngagement: Engagement,
    selectedEngagementSteps: EngagementStep[],
  ): boolean {
    if (
      Object.values(selectedEngagement.documentsInfo)
        .every(documentInfo => documentInfo.signatureRequirementType === SignatureRequirementType.ONE)
    ) {
      return !!Object.values(selectedEngagement.documentsInfo).map(documentInfo => documentInfo.documentContactId).pop();
    }

    return selectedEngagementSteps.some(({ tasks }) =>
      tasks.some(task => currentUserContactIds.includes(task.contactId) && task.type === EngagementTaskType.SIGN_ENGAGEMENT_DOCUMENT),
    );
  }

  @Selector([
    EngagementsState.getSelectedEngagementSteps,
    EngagementsState.getSelectedEngagementCurrentStep,
  ])
  static isUserTheFirstSignatory(
    selectedEngagementSteps: EngagementStep[],
    currentStep: EngagementStep,
  ) {
    return !selectedEngagementSteps
      .find(item => item.documentId === currentStep?.documentId)?.tasks
      .filter(task => [EngagementTaskType.SIGN_ENGAGEMENT_DOCUMENT, EngagementTaskType.CONFIRM_CONSENT_TASK, EngagementTaskType.CONFIRM_ADD_ON_TASK].includes(task.type))
      .some(task => isTaskCompleted(task.status));
  }

  @Selector([AppState.getUserContactIds, EngagementsState.getSelectedEngagementSteps])
  static isUserBillingContactOnSelectedEngagement(currentUserContactIds: number[], selectedEngagementSteps: EngagementStep[]): boolean {
    return selectedEngagementSteps.some(({ tasks }) =>
      tasks.some(task => currentUserContactIds.includes(task.contactId) && PAYMENT_INFO_TASKS.includes(task.type)),
    );
  }

  @Selector([AppState.getUserContactIds])
  static readUserContactIds(currentUserContactIds: number[]): number[] {
    return currentUserContactIds;
  }

  @Selector()
  static getMultipleIdentitySigningState({ multipleIdentitySigningState }: EngagementsStateModel): MultipleIdentitySigningState | undefined {
    return multipleIdentitySigningState;
  }

  @Selector([
    EngagementsState.getMultipleIdentitySigningState,
    EngagementsState.getSelectedEngagement,
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_MULTIPLE_IDENTITY),
  ])
  static isMultipleIdentitySigningActive(
    multiState: MultipleIdentitySigningState | undefined,
    selectedEngagement: Engagement | undefined,
    flagEnabled: boolean,
  ): boolean {
    const multiIdentityEnabled = selectedEngagement?.multipleIdentityEnabled ?? flagEnabled;
    return multiIdentityEnabled && (multiState?.contactProfiles?.length ?? 0) >= 2;
  }

  @Selector([EngagementsState.getMultipleIdentitySigningState])
  static getActiveSigningContactId(multiState: MultipleIdentitySigningState | undefined): number | null {
    return multiState?.activeSigningContactId ?? null;
  }

  @Selector([EngagementsState.getMultipleIdentitySigningState])
  static getSigningContactProfiles(multiState: MultipleIdentitySigningState | undefined): BaseContact[] {
    return multiState?.contactProfiles ?? [];
  }

  static getDocumentContactMapping(
    documentId: number,
    contactId: number | null,
  ): (multiState: MultipleIdentitySigningState | undefined) => EngagementDocumentContactMapping | undefined {
    return createSelector(
      [EngagementsState.getMultipleIdentitySigningState],
      (multiState) => multiState?.documentContactMappings?.find(mapping => mapping.engagementDocumentId === documentId && mapping.contactId === contactId),
    );
  }

  /**
   * Resolves a documentContactId to the contact's full name.
   * Returns undefined if not found in the multi-identity state.
   */
  static getContactNameByDocumentContactId(
    documentContactId: number,
  ): (multiState: MultipleIdentitySigningState | undefined) => string | undefined {
    return createSelector(
      [EngagementsState.getMultipleIdentitySigningState],
      (multiState) => {
        const mapping = multiState?.documentContactMappings?.find(mapping => mapping.id === documentContactId);
        
        if (!mapping) {
          return undefined;
        }

        return multiState?.contactProfiles?.find(profile => profile.id === mapping.contactId)?.contactInfo?.fullName;
      },
    );
  }

  /**
   * Resolves a documentContactId to the contact's ID (for switching active contact).
   */
  static getContactIdByDocumentContactId(
    documentContactId: number,
  ): (multiState: MultipleIdentitySigningState | undefined) => number | undefined {
    return createSelector(
      [EngagementsState.getMultipleIdentitySigningState],
      (multiState) => multiState?.documentContactMappings?.find(mapping => mapping.id === documentContactId)?.contactId,
    );
  }

  static getAllUserDocumentContactIds(
    documentId: number,
  ): (multiState: MultipleIdentitySigningState | undefined) => number[] {
    return createSelector(
      [EngagementsState.getMultipleIdentitySigningState],
      (multiState) => (multiState?.documentContactMappings ?? [])
        .filter(mapping => mapping.engagementDocumentId === documentId)
        .map(mapping => mapping.id),
    );
  }

  @Selector([EngagementsState.getSelectedEngagement, EngagementsState.getSelectedEngagementSteps])
  static isEngagementReadonly(selectedEngagement: Engagement, engagementSteps: EngagementStep[]): boolean {
    return selectedEngagement.isReadonly || engagementSteps.every(step => step.isReadonly);
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static isEngagementNonInteractive(selectedEngagement: Engagement): boolean {
    return NON_INTERACTIVE_ENGAGEMENT_STATUSES.includes(selectedEngagement?.status);
  }

  @Selector()
  static getEngagementsPendingChanges({ engagementsPendingChanges }: EngagementsStateModel): EngagementsPendingChanges {
    return engagementsPendingChanges;
  }

  @Selector([EngagementsState.getSelectedEngagement])
  static isEngagementLetterManuallyUploaded(selectedEngagement: Engagement): boolean {
    return selectedEngagement.isEngagementManuallyUploaded;
  }

  static getEngagementPendingChanges(
    engagementId: number,
  ): (engagementsPendingChanges: EngagementsPendingChanges) => EngagementPendingChanges {
    return createSelector(
      [EngagementsState.getEngagementsPendingChanges],
      engagementsPendingChanges => engagementsPendingChanges[engagementId] ?? {} as EngagementPendingChanges,
    );
  }

  static getEngagementStepPendingChanges(
    engagementId: number,
    stepType: EngagementStepType,
  ): (engagementPendingChanges: EngagementPendingChanges) => EngagementStepPendingChanges {
    return createSelector(
      [EngagementsState.getEngagementPendingChanges(engagementId)],
      engagementsPendingChanges => engagementsPendingChanges[stepType] ?? {},
    );
  }

  @Selector([
    EngagementsState.getSelectedEngagement,
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_AUTOMATED_PAYMENT),
  ])
  static isSelectedEngagementAutoDraft(selectedEngagement: Engagement, isAutoDraftFeatureEnabled: boolean): boolean {
    return isAutoDraftFeatureEnabled && EngagementsState.isAutoDraftEngagement(selectedEngagement);
  }

  static getIsAutoDraftEngagement(engagement: Engagement): (isAutoDraftFeatureEnabled: boolean) => boolean {
    return createSelector(
      [LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_AUTOMATED_PAYMENT)],
      isAutoDraftFeatureEnabled => isAutoDraftFeatureEnabled && EngagementsState.isAutoDraftEngagement(engagement),
    );
  }

  constructor(
    private readonly store: Store,
    private readonly engagementApiService: EngagementApiService,
    private readonly engagementsApiService: EngagementsApiService,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly configService: ConfigService,
    private readonly uploadApiService: UploadApiService,
    private readonly authService: AuthService,
    private readonly contactApiService: ContactApiService,
    private readonly logger: Logger,
  ) { }

  @Action(EngagementAvaliabilityCheck)
  avaliabilityCheck({ dispatch }: StateContext<EngagementsStateModel>) {
    return this.authStateAdaptor.loginComplete().pipe(
     ignoreFalsy(),
     map(() => this.configService.engagementsEnabled),
     withLatestFrom(this.authService.getUser()),
     switchMap(([engagementsEnabled, user]) => {
        if (!engagementsEnabled || user.isAnonymous()) {
          return dispatch(new ShowEngagementsTab(false));
        }

        return this.engagementsApiService.hasEngagements().pipe(
          switchMap(hasEngagements => dispatch(new ShowEngagementsTab(hasEngagements))),
        );
      }),
    );
  }

  @Action(GetEngagementHub)
  getEngagementHub(
    { patchState, dispatch }: StateContext<EngagementsStateModel>,
    { engagementId, forceRefresh }: GetEngagementHub,
  ): Observable<Engagement | undefined> {
    if (!forceRefresh) {
      const savedEngagemenData = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
      patchState({ selectedEngagement: { status: LoadableStatus.LOADING, data: savedEngagemenData } });
    }

    const loggedInUserContactId = this.store.selectSnapshot(AppState.getLoggedInUserContactId);

    return this.engagementsApiService.getEngagementHub(engagementId, loggedInUserContactId).pipe(
      map((hub) => {
        const isEngagementVisible = !!hub && VALID_ENGAGEMENT_STATUSES.includes(hub.status);

        if (!isEngagementVisible) {
          return { hub: undefined, mappedEngagement: undefined };
        }

        return { hub, mappedEngagement: EngagementsState.mapHubToEngagement(hub) };
      }),
      tap({
        next: ({ hub, mappedEngagement }) => {
          patchState({ selectedEngagement: { status: LoadableStatus.LOADED, data: mappedEngagement } });
          this.triggerMultipleIdentitySigningLoadIfNeeded(dispatch, patchState, mappedEngagement, hub);
        },
        error: () => patchState({ selectedEngagement: { status: LoadableStatus.ERROR } }),
      }),
      map(({ mappedEngagement }) => mappedEngagement),
      take(1),
    );
  }

  private triggerMultipleIdentitySigningLoadIfNeeded(
    dispatch: StateContext<EngagementsStateModel>['dispatch'],
    patchState: StateContext<EngagementsStateModel>['patchState'],
    engagement: Engagement | undefined,
    hub: EngagementHub | undefined,
  ): void {
    // Clear stale multi-identity state from a previous engagement before deciding whether
    // to load fresh data. Without this, navigating from a multi-contact engagement to a
    // single-contact one (or to one with the FF off) would leave isMultipleIdentitySigningActive
    // erroneously true via the old contactProfiles, routing submissions through the
    // multi-contact endpoints which then bail with empty unsignedContactIds — appearing
    // to "skip" the step.
    patchState({ multipleIdentitySigningState: undefined });

    if (!engagement) {
      return;
    }

    const multiIdentityEnabled = engagement.multipleIdentityEnabled
      ?? this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_MULTIPLE_IDENTITY));

    if (!multiIdentityEnabled) {
      return;
    }

    // Secure-link users deliberately skip the email-based userContactIds lookup, so we derive
    // mappings locally from the hub steps (which already encode documentContactId + contactId
    // per allowed slot) and intersect with the grants the link authorizes. This enables the
    // dropdown without broadening the secure-link's data scope.
    const authUser = this.authService.getUserSnapshot();

    if (authUser?.isSecureLinkUser()) {
      this.triggerMultipleIdentityLoadForSecureLink(dispatch, hub, authUser.getSecureLinkGrants());
      return;
    }

    const userContactIds = this.store.selectSnapshot(AppState.getUserContactIds);

    if (userContactIds.length < 2) {
      return;
    }

    if (!hub) {
      return;
    }

    // Derive synchronously from hub.steps — going async here let docs filter annotations before the dispatch landed.
    const allowedContactIds = new Set(userContactIds);
    const now = new Date().toISOString();

    const mappings: EngagementDocumentContactMapping[] = (hub.steps ?? [])
      .filter(step => step.documentId != null && step.tasks?.some(task => allowedContactIds.has(task.contactId)))
      .map(step => {
        const { documentContactId, documentId, tasks } = step;
        const matchingTask = tasks.find(task => allowedContactIds.has(task.contactId))!;
        const isSigned = tasks.every(task => isTaskCompleted(task.status));

        return {
          id: documentContactId,
          engagementDocumentId: documentId!,
          contactId: matchingTask.contactId,
          signedAt: isSigned ? now : null,
        };
      });

    const signatoryContactIds = [...new Set(mappings.map(mapping => mapping.contactId))];

    if (signatoryContactIds.length < 2) {
      return;
    }

    dispatch(new LoadMultipleIdentitySigningData(mappings, signatoryContactIds));
  }

  private triggerMultipleIdentityLoadForSecureLink(
    dispatch: StateContext<EngagementsStateModel>['dispatch'],
    hub: EngagementHub | undefined,
    grants: SecureLinkAuthorization | undefined,
  ): void {
    if (!hub || !grants?.grants?.length) {
      return;
    }

    const allowedDocContactIds = new Set(
      grants.grants
        .filter(grant => grant.active && grant.secureLinkEntityType === SecureLinkEntityType.ENGAGEMENT_DOCUMENT_CONTACT)
        .map(grant => grant.entityId),
    );

    if (allowedDocContactIds.size < 2) {
      return;
    }

    // Derive (documentContactId, contactId, engagementDocumentId) tuples from hub steps.
    // When multi-identity is active, the hub emits one step per (document, contact).
    const mappings: EngagementDocumentContactMapping[] = (hub.steps ?? [])
      .filter(step => step.documentId != null && allowedDocContactIds.has(step.documentContactId))
      .map(step => {
        const matchingTask = step.tasks?.find(task => task.documentId === step.documentId) ?? step.tasks?.[0];
        return matchingTask?.contactId != null
          ? {
              id: step.documentContactId,
              engagementDocumentId: step.documentId,
              contactId: matchingTask.contactId,
              signedAt: null,
            } as EngagementDocumentContactMapping
          : null;
      })
      .filter((mapping): mapping is EngagementDocumentContactMapping => mapping !== null);

    const distinctContactIds = [...new Set(mappings.map(mapping => mapping.contactId))];

    if (distinctContactIds.length < 2) {
      return;
    }

    dispatch(new LoadMultipleIdentitySigningData(mappings, distinctContactIds));
  }

  @Action([SetTableSearchValue])
  setTableSearchValue({ patchState }: StateContext<EngagementsStateModel>, { searchValue }: SetTableSearchValue): void {
    patchState({ tableSearchValue: searchValue });
  }

  @Action([SetSelectedEngagementId])
  setSelectedEngagementId({ patchState }: StateContext<EngagementsStateModel>, { engagementId }: SetSelectedEngagementId): void {
    patchState({ selectedEngagementId: engagementId });
  }

  @Action([SetNonInteractiveEngagement])
  setNonInteractiveEngagement({ patchState }: StateContext<EngagementsStateModel>, { engagement }: SetNonInteractiveEngagement): void {
    const mappedEngagement = EngagementsState.mapMinimalHubToEngagement(engagement);
    patchState({
      selectedEngagementId: engagement.id,
      selectedEngagement: { status: LoadableStatus.LOADED, data: mappedEngagement },
    });
  }

  @Action([ViewEngagement])
  viewEngagement({ dispatch }: StateContext<EngagementsStateModel>, { engagementId }: ViewEngagement): Observable<any> | void {
    return this.store.select(EngagementsState.getSelectedEngagementStatus).pipe(
      // wait until loading completes, but exit observable without continuing on error or invalid engagement ID
      filter(status => status && !LoadableHelper.isLoading(status)),
      take(1),
      ignoreFalsy(),
      switchMap(_ => dispatch(new SetSelectedEngagementId(engagementId))),
      switchMap(_ => {
        const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);

        return dispatch(new GetEngagementHub(selectedEngagement.id));
      }),
      take(1),
      filter(_ => this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef) == null),
      switchMap(_ => dispatch(new MoveToEngagementStep(undefined))),
    );
  }

  @Action([GetEngagementDocument])
  getSelectedEngagementDocument(
    { setState, patchState }: StateContext<EngagementsStateModel>,
    { fileId, documentType, forceRefresh }: GetEngagementDocument,
  ): Observable<EditableFileResponse> {
    const status = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocumentStatus(fileId));

    if (!(LoadableHelper.shouldRequest(status) || forceRefresh)) {
      return;
    }

    // Resolve engagementId and documentId from the selected engagement state
    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const engagementId = selectedEngagement?.id;

    // Find the documentId that corresponds to this fileId
    const documentEntry = Object.values(selectedEngagement?.documentsInfoById ?? {})
      .find(entry => entry.file?.id === fileId);
    const documentId = documentEntry?.documentId;

    if (!engagementId || !documentId) {
      // Fallback to legacy upload service if engagement/document IDs are not available
      return this.uploadApiService.getDownloadUrl(fileId).pipe(
        switchMap((file) => this.uploadApiService.getDownloadFileResponse(file.url).pipe(
          switchMap(response => blobToBase64(response.body).pipe(
            map(base64Content => ({
              mdfHash: bytesToBase64(hexToBytes((response.headers.get('etag') ?? '').replace(/^"(.*)"$/, '$1'))),
              isEdited: false,
              fileId,
              fileName: file.fileName,
              url: file.url,
              contentType: response.headers.get('Content-Type'),
              base64Content,
              addedSignatureIds: [],
            })),
          )),
        )),
        tap({
          next: fileResponse => setState(patch({ selectedDocuments: patch({ [fileId]: { status: LoadableStatus.LOADED, data: fileResponse } }) })),
          error: () => setState(patch({ selectedDocuments: patch({ [fileId]: { status: LoadableStatus.ERROR } }) })),
        }),
      );
    }

    // Use the new REST document endpoint
    const loggedInUserContactId = this.store.selectSnapshot(AppState.getLoggedInUserContactId);

    return this.engagementsApiService.getEngagementDocument(engagementId, documentId, loggedInUserContactId).pipe(
      switchMap((hubDocument) => {
        // If consent settings are present, populate the consent settings state
        if (hubDocument.consentSettings) {
          patchState({
            selectedEngagementConsentSettings: {
              id: hubDocument.documentId,
              introduction: hubDocument.consentSettings.introduction,
              affirmativeConsentLanguage: hubDocument.consentSettings.affirmativeLanguage,
              negativeConsentLanguage: hubDocument.consentSettings.negativeLanguage,
            },
          });
        }

        // Download the PDF from the downloadUrl and convert to base64
        return this.uploadApiService.getDownloadFileResponse(hubDocument.downloadUrl).pipe(
          switchMap(response => blobToBase64(response.body).pipe(
            map(base64Content => ({
              mdfHash: bytesToBase64(hexToBytes((response.headers.get('etag') ?? '').replace(/^"(.*)"$/, '$1'))),
              isEdited: false,
              fileId,
              fileName: hubDocument.downloadUrl.split('/').pop()?.split('?')[0] ?? `document-${documentId}.pdf`,
              url: hubDocument.downloadUrl,
              contentType: response.headers.get('Content-Type'),
              base64Content,
              addedSignatureIds: [],
            })),
          )),
        );
      }),
      tap({
        next: fileResponse => setState(patch({ selectedDocuments: patch({ [fileId]: { status: LoadableStatus.LOADED, data: fileResponse } }) })),
        error: () => setState(patch({ selectedDocuments: patch({ [fileId]: { status: LoadableStatus.ERROR } }) })),
      }),
    );
  }

  @Action([GetEngagementConsentSettings])
  getEngagementConsent(
    _ctx: StateContext<EngagementsStateModel>,
    _action: GetEngagementConsentSettings,
  ): void {
    // Consent settings are now populated by GetEngagementDocument via the REST document endpoint.
    // This action is kept as a no-op for backward compatibility with AbstractApryseStep dispatch.
  }

  @Action([GetSelectedEngagementJobs])
  getSelectedEngagementJobs(
    { patchState }: StateContext<EngagementsStateModel>,
    { engagementId }: GetSelectedEngagementJobs,
  ): Observable<EngagementJobResponse[]> {
    return this.engagementApiService.getEngagementJobs(engagementId).pipe(
        take(1),
        setLoadableInState( patchState, 'selectedEngagementJobs'),
      );
  }

  @Action([GetSelectedEngagementPrepayment])
  getSelectedEngagementPrepayment(
    { patchState, dispatch }: StateContext<EngagementsStateModel>,
    { engagementId, setStepAsFailedIfApplicable }: GetSelectedEngagementPrepayment,
  ): Observable<EngagementPrepaymentResponse | void> {
    return this.engagementApiService.getEngagementPrepayment(engagementId).pipe(
      // ART-8130 (AC3): the authoritative prepaymentStatus ships on the hub, not on the Hasura prepayments
      // query. Re-source it from the selected engagement (mapped from the hub) onto the response so the
      // payment step's derivePrepaymentStepState keeps reading it off the prepayment unchanged.
      map(engagementPrepayment => EngagementsState.mergeHubPrepaymentStatus(
        engagementPrepayment,
        this.store.selectSnapshot(EngagementsState.getSelectedEngagement),
      )),
      tap(
        engagementPrepayment => patchState({ selectedEngagementPrepayment: { status: LoadableStatus.LOADED, data: engagementPrepayment } }),
        _ => patchState({ selectedEngagementPrepayment: { status: LoadableStatus.ERROR } }),
      ),
      switchMap(engagementPrepayment =>
        // TODO: setStepAsFailedIfApplicable is only temporary until we implement a fully synchronous engagement prepayment flow, only show FAIL on initial load
        setStepAsFailedIfApplicable && (engagementPrepayment && engagementPrepayment.active === false)
          ? dispatch(new SetSelectedEngagementPrepaymentAsFailed())
          : of(engagementPrepayment),
      ),
      take(1),
    );
  }

  @Action([SetSelectedEngagementPrepaymentAsFailed])
  setSelectedEngagementPrepaymentAsFailed({ patchState }: StateContext<EngagementsStateModel>): void {
    const engagementWithReportLoadable = this.store.selectSnapshot(EngagementsState.getSelectedEngagementLoadable);

    patchState({
      selectedEngagement: {
        ...engagementWithReportLoadable,
        data: {
          ...engagementWithReportLoadable.data,
          report: {
            ...engagementWithReportLoadable.data.report,
            steps: engagementWithReportLoadable.data.report.steps.map(step => {
              if (step.type !== EngagementStepType.PAYMENT) {
                return step;
              }

              return { ...step, status: EngagementStepStatus.FAILED };
            }),
          },
        },
      },
    });
  }

  @Action([ConfirmEngagementViewed])
  confirmEngagementViewed({ patchState }: StateContext<EngagementsStateModel>): Observable<number> {
    const engagementContactIds = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps).reduce(
      (contactIds, step) => contactIds.concat(step.tasks.map(({ contactId }) => contactId)),
      new Array<number>(),
    );

    const userContactIds = this.store.selectSnapshot(AppState.getUserContactIds);
    const userContactIdForEngagement = engagementContactIds.find(engagementContactId => userContactIds.includes(engagementContactId));

    const markAsViewed = () => {
      const selectedEngagementWithReportLoadable = this.store.selectSnapshot(EngagementsState.getSelectedEngagementLoadable);
      patchState({
        selectedEngagement: {
          ...selectedEngagementWithReportLoadable,
          data: {
            ...selectedEngagementWithReportLoadable.data,
            report: {
              ...selectedEngagementWithReportLoadable.data.report,
              currentUserHasViewedEngagement: true,
            },
          },
        },
      });
    };

    if (!userContactIdForEngagement) {
      markAsViewed();
      return of(null);
    }

    return this.engagementApiService.confirmEngagementViewed(
      this.store.selectSnapshot(EngagementsState.getSelectedEngagementId),
      userContactIdForEngagement,
    ).pipe(
      tap(_ => markAsViewed()),
    );
  }

  @Action([RemoveSelectedEngagementData])
  removeSelectedEngagementData({ patchState }: StateContext<EngagementsStateModel>): void {
    patchState({
      selectedEngagementId: undefined,
      selectedDocuments: {},
      selectedEngagement: { status: LoadableStatus.DEFAULT },
      selectedEngagementJobs: { status: LoadableStatus.DEFAULT },
      selectedEngagementPrepayment: { status: LoadableStatus.DEFAULT },
    });
  }

  @Action([SetEngagementCurrentStepRef])
  setEngagementCurrentStepRef({ patchState }: StateContext<EngagementsStateModel>, { engagementId, ref }: SetEngagementCurrentStepRef): void {
    const current = this.store.selectSnapshot(EngagementsState.getEngagementsCurrentStepRef);
    const currentRef = current?.[engagementId];

    // Don't update if no change will happen (both type and documentId equal)
    if (currentRef != null && ref != null && currentRef.type === ref.type && currentRef.documentId === ref.documentId) {
      return;
    }

    // Force override (when wizard step change is triggered)
    if (ref != null) {
      patchState({ engagementsCurrentStepRef: { ...current, [engagementId]: ref } });
      return;
    }

    // Fallback to engagement letter step when current ref is invalid/hidden
    const selectedSteps = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps);
    const matchingStep = selectedSteps?.find(step => step.type === currentRef?.type);
    const hasValidDocument = currentRef?.documentId == null || matchingStep?.tasks?.some(t => (t as any).documentId === currentRef.documentId);
    const shouldFallback = currentRef == null || matchingStep == null || matchingStep.hidden || !hasValidDocument;

    const nextRef = shouldFallback ? { type: EngagementStepType.LETTER } : currentRef;

    // Don't update if still equal after validation
    if (
      currentRef != null &&
      currentRef.type === nextRef.type && currentRef.documentId === nextRef.documentId
    ) {
      return;
    }

    patchState({ engagementsCurrentStepRef: { ...current, [engagementId]: nextRef } });
  }

  /**
   * Clears the document cache for a specific step to ensure fresh data when navigating
   */
  private clearDocumentCacheForStep(
    patchState: StateContext<EngagementsStateModel>['patchState'],
    getState: StateContext<EngagementsStateModel>['getState'],
    stepReference: EngagementStepRef
  ): void {
    if (stepReference.documentId) {
      const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
      const documentInfo = engagement.documentsInfoById?.[stepReference.documentId];
      if (documentInfo) {
        patchState({
          selectedDocuments: {
            ...getState().selectedDocuments,
            [documentInfo.file.id]: { status: LoadableStatus.DEFAULT }
          }
        });
      }
    }
  }

  /**
   * Navigates to a specific step with document cache clearing and step reference setting
   */
  private navigateToStep(
    dispatch: StateContext<EngagementsStateModel>['dispatch'],
    patchState: StateContext<EngagementsStateModel>['patchState'],
    getState: StateContext<EngagementsStateModel>['getState'],
    selectedEngagementId: number,
    stepReference: EngagementStepRef
  ): Observable<any> {
    // Clear document cache for the target step to ensure fresh data
    this.clearDocumentCacheForStep(patchState, getState, stepReference);

    return dispatch(new SetEngagementCurrentStepRef(selectedEngagementId, stepReference));
  }

  @Action([MoveToEngagementStep])
  moveToEngagementStep(
    { dispatch, patchState, getState }: StateContext<EngagementsStateModel>,
    { destinationStep }: MoveToEngagementStep,
  ): Observable<any> {
    const selectedEngagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
    const visibleEngagementSteps = this.store.selectSnapshot(EngagementsState.getSelectedEngagementVisibleSteps);

    if (!visibleEngagementSteps.length) {
      return this.navigateToStep(dispatch, patchState, getState, selectedEngagementId, { type: EngagementStepType.LETTER });
    }

    let stepReference: EngagementStepRef;

    // should only happen on initial engagement load during session
    if (destinationStep == null) {
      const firstIncompleteStep = visibleEngagementSteps.find(step => step.status !== EngagementStepStatus.COMPLETE) ?? visibleEngagementSteps[0];
      const firstIncompleteDocumentId = (firstIncompleteStep.tasks?.find(task => task.documentId))?.documentId;
      stepReference = { type: firstIncompleteStep.type, documentId: firstIncompleteDocumentId };
    } else if (destinationStep === 'previous') {
      const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);

      const findCurrentStepIndex = currentStep.type !== EngagementStepType.PAYMENT ? ({ type, tasks }) => type === currentStep?.type && (
        currentStep?.tasks?.length ? tasks?.some(task => task.documentId && currentStep.tasks.some(currentStepTask => currentStepTask.documentId === task.documentId)) : true
      ) : ({ type }) => type === currentStep?.type;

      const currentStepIndex = visibleEngagementSteps.findIndex(findCurrentStepIndex);
      const previousStep = visibleEngagementSteps[currentStepIndex - 1];
      stepReference = { type: previousStep.type, documentId: (previousStep.tasks?.find(task => task.documentId))?.documentId };
    } else if (destinationStep === 'next') {
      const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);
      const currentStepIndex = visibleEngagementSteps.findIndex(({ type, tasks }) => type === currentStep?.type && (
        currentStep?.tasks?.length ? tasks?.some(task => task.documentId && currentStep.tasks.some(currentStepTask => currentStepTask.documentId === task.documentId)) : true
      ));
      const nextStep = visibleEngagementSteps[currentStepIndex + 1];
      stepReference = { type: nextStep.type, documentId: (nextStep.tasks?.find(task => task.documentId))?.documentId };
    } else if (typeof destinationStep === 'string') {
      const step = visibleEngagementSteps.find(step => step.type === destinationStep);
      stepReference = { type: step.type, documentId: (step.tasks?.find(t => (t as any).documentId) as any)?.documentId };
    } else {
      stepReference = destinationStep as EngagementStepRef;
    }

    return this.navigateToStep(dispatch, patchState, getState, selectedEngagementId, stepReference);
  }

  @Action([SaveEngagementLetter])
  saveEngagementLetter(
    { dispatch }: StateContext<EngagementsStateModel>,
    { updatedEngagementLetterData }: SaveEngagementLetter,
  ): Observable<unknown> {
    if (!updatedEngagementLetterData) {
      throw new Error('Document has not been signed, cannot update.');
    }

    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const documentInfo = selectedEngagement.documentsInfo[EngagementDocumentType.ENGAGEMENT_LETTER];
    const selectedEngagementLetter: EditableFileResponse = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocument(documentInfo.file.id));

    if (selectedEngagementLetter == null) {
      return;
    }

    const signatureMetadata = SignatureService.createSignatureMetadata(selectedEngagementLetter.mdfHash);
    return this.engagementApiService.signEngagementDocument(
      selectedEngagement.documentsInfo[EngagementDocumentType.ENGAGEMENT_LETTER].documentContactId,
      selectedEngagementLetter.mdfHash,
      selectedEngagementLetter.fileName,
      updatedEngagementLetterData,
      selectedEngagementLetter.fileId,
      signatureMetadata
    ).pipe(
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      switchMap(_ => dispatch(new GetEngagementDocument(documentInfo.file.id, EngagementStepType.LETTER))),
      take(1),
    );
  }

  @Action([SaveConsentDocument])
  saveConsentDocument(
    { dispatch }: StateContext<EngagementsStateModel>,
    { updatedDocumentData, consent }: SaveConsentDocument
  ): Observable<void> {
    if (!updatedDocumentData) {
      throw new Error('Document has not been signed, cannot update.');
    }

    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const currentStepRef = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef);

    // Use the current step's document info instead of hardcoded consent document
    const documentInfo = currentStepRef?.documentId
      ? selectedEngagement.documentsInfoById?.[currentStepRef.documentId]
      : selectedEngagement.documentsInfo[EngagementDocumentType.CONSENT_DOCUMENT];

    const selectedEngagementLetter: EditableFileResponse = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocument(documentInfo.file.id));

    if (selectedEngagementLetter == null) {
      return;
    }

    const signatureMetadata = SignatureService.createSignatureMetadata(selectedEngagementLetter.mdfHash);
    return this.engagementApiService.signEngagementConsentDocument(
      documentInfo.documentContactId,
      selectedEngagementLetter.mdfHash,
      selectedEngagementLetter.fileName,
      updatedDocumentData,
      consent,
      selectedEngagementLetter.fileId,
      signatureMetadata
    ).pipe(
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      switchMap(_ => dispatch(new GetEngagementDocument(documentInfo.file.id, (currentStepRef?.type as any) || EngagementStepType.CONSENT))),
      take(1),
    );
  }

  @Action([ConfirmConsentDocument])
  confirmConsentDocument(
    { dispatch }: StateContext<EngagementsStateModel>,
  ): void | Observable<void> {
    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const currentStepRef = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef);

    // Use the current step's document info instead of hardcoded consent document
    const documentInfo = currentStepRef?.documentId
      ? selectedEngagement.documentsInfoById?.[currentStepRef.documentId]
      : selectedEngagement.documentsInfo[EngagementDocumentType.CONSENT_DOCUMENT];

    const selectedEngagementLetter: EditableFileResponse = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocument(documentInfo.file.id));

    if (selectedEngagementLetter == null) {
      return;
    }

    return this.engagementApiService.confirmConsent(documentInfo.documentContactId).pipe(
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      switchMap(_ => dispatch(new GetEngagementDocument(documentInfo.file.id, (currentStepRef?.type as any) || EngagementStepType.CONSENT))),
      take(1),
    );
  }

  @Action([UpdateSelectedDocument])
  updateSelectedDocument(
    { setState }: StateContext<EngagementsStateModel>,
    { updatedDocumentBase64, documentType, addedSignatureId }: UpdateSelectedDocument,
  ): void {
    // We need to find the fileId for the current document type
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const documentInfo = engagement.documentsInfo[STEP_LABEL_DOCUMENT_TYPE_MAPPING[documentType]];
    const fileId = documentInfo.file.id;

    const currentEngagementLetterLoadable = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocumentLoadable(fileId));
    if (currentEngagementLetterLoadable.data == null || updatedDocumentBase64 === currentEngagementLetterLoadable.data.base64Content) {
      return;
    }

    const addedSignatureIds = currentEngagementLetterLoadable.data.addedSignatureIds.concat(addedSignatureId ?? []);

    setState(
      patch({
        selectedDocuments: patch({
          [fileId]: {
            ...currentEngagementLetterLoadable,
            data: {
              ...currentEngagementLetterLoadable.data,
              base64Content: updatedDocumentBase64,
              isEdited: true,
              addedSignatureIds,
            },
          },
        })
      }),
    );
  }

  @Action([ConfirmPaymentInfo])
  confirmPaymentInfo({ dispatch }: StateContext<EngagementsStateModel>, { paymentMethod }: ConfirmPaymentInfo): Observable<number | void> {
    const engagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
    const billingContactId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps)
      .find(step => step.type === EngagementStepType.PAYMENT).tasks
      .find(({ type }) => type === EngagementTaskType.CONFIRM_PAYMENT_INFO).contactId;

    return this.engagementApiService.confirmPaymentInfo(engagementId, billingContactId, paymentMethod).pipe(
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
    );
  }

  @Action([RecoverFromEngagementCompletionFailure])
  recoverFromEngagementCompletionFailure(
    {}: StateContext<EngagementsStateModel>,
    { stepSubmissionError }: RecoverFromEngagementCompletionFailure,
  ): Observable<any> {
    const selectedEngagementSteps = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps);
    const isSubmittingLastStep = selectedEngagementSteps.filter(({ status }) => status !== EngagementStepStatus.COMPLETE).length === 1;
    const isSelectedEngagementPrepaymentRequired = this.store.selectSnapshot(EngagementsState.isSelectedEngagementPrepaymentRequired);

    if (!isSubmittingLastStep || !isSelectedEngagementPrepaymentRequired) {
      return throwError(stepSubmissionError);
    }

    const engagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);

    // PP-3809: refresh the hub before fetching the prepayment. GetSelectedEngagementPrepayment merges the
    // decline code from the selected-engagement snapshot (mergeHubPrepaymentStatus), so dispatching both
    // concurrently let the merge read the stale pre-charge engagement and the failure banner degraded to
    // the generic "an unexpected error" copy instead of the specific decline reason.
    return this.store.dispatch(new GetEngagementHub(engagementId)).pipe(
      switchMap(() => this.store.dispatch(new GetSelectedEngagementPrepayment(engagementId, false))),
    );
  }

  @Action([SetEngagementStepPendingChanges])
  setEngagementStepPendingChanges(
    { patchState }: StateContext<EngagementsStateModel>,
    { engagementId, stepType, pendingChanges }: SetEngagementStepPendingChanges,
  ): void {
    const engagementsPendingChanges = this.store.selectSnapshot(EngagementsState.getEngagementsPendingChanges);
    if (engagementsPendingChanges[engagementId] == null) {
      patchState({
        engagementsPendingChanges: {
          ...engagementsPendingChanges,
          [engagementId]: {
            [stepType]: pendingChanges,
          },
        },
      });

      return;
    }

    patchState({
      engagementsPendingChanges: Object.entries(engagementsPendingChanges).reduce(
        (acc, [existingEngagementId, existingEngagementPendingChanges]) => {
          if (+existingEngagementId !== engagementId) {
            return {
              ...acc,
              [existingEngagementId]: existingEngagementPendingChanges,
            };
          }

          return {
            ...acc,
            [existingEngagementId]: {
              ...existingEngagementPendingChanges,
              [stepType]: {
                ...(existingEngagementPendingChanges[stepType] ?? {}),
                ...pendingChanges,
              },
            },
          };
        },
        {} as EngagementsPendingChanges,
      ),
    });
  }

  @Action([RemoveEngagementStepPendingChanges])
  removeEngagementStepPendingChanges(
    { patchState }: StateContext<EngagementsStateModel>,
    { engagementId, stepType }: RemoveEngagementStepPendingChanges,
  ): void {
    const engagementsPendingChanges = structuredClone(this.store.selectSnapshot(EngagementsState.getEngagementsPendingChanges));

    if (engagementsPendingChanges[engagementId] == null) {
      return;
    }

    if (
      // only engagement is specified
      stepType == null ||
      // the pending changes for the step specified are the only ones left
      (Object.keys(engagementsPendingChanges[engagementId]).length === 1 && engagementsPendingChanges[engagementId][stepType] != null)
    ) {
      // remove all pending changes for specified engagement
      delete engagementsPendingChanges[engagementId];
    } else {
      // remove pending changes for engagement's specified step
      delete engagementsPendingChanges[engagementId]?.[stepType];
    }

    patchState({ engagementsPendingChanges });
  }

  @Action([ResetState])
  resetState({ patchState }: StateContext<EngagementsStateModel>): void {
    patchState(DEFAULT_ENGAGEMENT_STATE_MODEL);
  }

  // --- Multiple Identity Signing action handlers ---

  @Action([LoadMultipleIdentitySigningData])
  loadMultipleIdentitySigningData(
    { patchState }: StateContext<EngagementsStateModel>,
    { documentContactMappings, contactIds }: LoadMultipleIdentitySigningData,
  ): Observable<void> {
    // Only need to fetch contact profiles for display names -- mappings come from hub steps
    return this.contactApiService.getBaseContacts(contactIds).pipe(
      tap((profiles) => {
        const signatoryContactIds = new Set(documentContactMappings.map(mapping => mapping.contactId));
        const relevantProfiles = profiles.filter(profile => signatoryContactIds.has(profile.id));

        if (relevantProfiles.length < 2) {
          patchState({ multipleIdentitySigningState: undefined });
          return;
        }

        const loggedInContactId = this.store.selectSnapshot(AppState.getLoggedInUserContactId);
        
        patchState({
          multipleIdentitySigningState: {
            documentContactMappings,
            activeSigningContactId: signatoryContactIds.has(loggedInContactId) ? loggedInContactId : relevantProfiles[0].id,
            contactProfiles: relevantProfiles,
          },
        });
      }),
      map(() => undefined),
      catchError((error) => {
        this.logger.error('EngagementsState : LoadMultipleIdentitySigningData : Error', error);
        patchState({ multipleIdentitySigningState: undefined });
        return of(undefined);
      }),
    );
  }

  @Action([SetActiveSigningContact])
  setActiveSigningContact(
    { patchState, getState }: StateContext<EngagementsStateModel>,
    { contactId }: SetActiveSigningContact,
  ): void {
    const currentState = getState().multipleIdentitySigningState;
    if (!currentState) return;

    patchState({
      multipleIdentitySigningState: {
        ...currentState,
        activeSigningContactId: contactId,
      },
    });
  }

  /**
   * Batch multi-contact engagement letter signing (ART-7511).
   * Uploads the signed PDF once and marks every unsigned contact atomically.
   */
  @Action([SaveEngagementLetterMultiContact])
  saveEngagementLetterMultiContact(
    { dispatch, getState, setState }: StateContext<EngagementsStateModel>,
    { updatedEngagementLetterData }: SaveEngagementLetterMultiContact,
  ): Observable<unknown> {
    if (!updatedEngagementLetterData) {
      throw new Error('Document has not been signed, cannot update.');
    }

    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const documentInfo = selectedEngagement?.documentsInfo[EngagementDocumentType.ENGAGEMENT_LETTER];

    if (!documentInfo) {
      return of(null);
    }

    const selectedDoc = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocument(documentInfo.file.id));

    if (!selectedDoc) {
      return of(null);
    }

    const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
    const unsignedContactIds = (multiState?.documentContactMappings ?? [])
      .filter(mapping => mapping.engagementDocumentId === documentInfo.documentId && !mapping.signedAt)
      .map(mapping => mapping.id);

    if (unsignedContactIds.length === 0) {
      return of(null);
    }

    const metadata = SignatureService.createSignatureMetadata(selectedDoc.mdfHash);

    return this.engagementApiService.signEngagementDocumentMultiContact(
      unsignedContactIds,
      selectedDoc.mdfHash,
      selectedDoc.fileName,
      updatedEngagementLetterData,
      selectedDoc.fileId,
      metadata,
    ).pipe(
      tap(() => this.markDocumentContactsAsSigned(getState, setState, unsignedContactIds)),
      switchMap(() => dispatch(new GetEngagementDocument(documentInfo.file.id, EngagementStepType.LETTER))),
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      take(1),
    );
  }

  /**
   * Batch multi-contact consent / addon document signing (ART-7511).
   */
  @Action([SaveConsentDocumentMultiContact])
  saveConsentDocumentMultiContact(
    { dispatch, getState, setState }: StateContext<EngagementsStateModel>,
    { updatedDocumentData, consent }: SaveConsentDocumentMultiContact,
  ): Observable<unknown> {
    if (!updatedDocumentData) {
      throw new Error('Document has not been signed, cannot update.');
    }

    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const currentStepRef = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef);

    const documentInfo = currentStepRef?.documentId
      ? selectedEngagement?.documentsInfoById?.[currentStepRef.documentId]
      : selectedEngagement?.documentsInfo[EngagementDocumentType.CONSENT_DOCUMENT];

    if (!documentInfo) {
      return of(null);
    }

    const selectedDoc = this.store.selectSnapshot(EngagementsState.getSelectedEngagementDocument(documentInfo.file.id));

    if (!selectedDoc) {
      return of(null);
    }

    const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
    const unsignedContactIds = (multiState?.documentContactMappings ?? [])
      .filter(mapping => mapping.engagementDocumentId === documentInfo.documentId && !mapping.signedAt)
      .map(mapping => mapping.id);

    if (unsignedContactIds.length === 0) {
      return of(null);
    }

    const stepType = (currentStepRef?.type as any) || EngagementStepType.CONSENT;
    const metadata = SignatureService.createSignatureMetadata(selectedDoc.mdfHash);

    return this.engagementApiService.signEngagementConsentDocumentMultiContact(
      unsignedContactIds,
      selectedDoc.mdfHash,
      selectedDoc.fileName,
      updatedDocumentData,
      consent,
      selectedDoc.fileId,
      metadata,
    ).pipe(
      tap(() => this.markDocumentContactsAsSigned(getState, setState, unsignedContactIds)),
      switchMap(() => dispatch(new GetEngagementDocument(documentInfo.file.id, stepType))),
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      take(1),
    );
  }

  /**
   * Batch multi-contact consent / addon document skip (declined for every contact).
   */
  @Action([ConfirmConsentDocumentMultiContact])
  confirmConsentDocumentMultiContact(
    { dispatch, getState, setState }: StateContext<EngagementsStateModel>,
  ): Observable<unknown> {
    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const currentStepRef = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef);

    const documentInfo = currentStepRef?.documentId
      ? selectedEngagement?.documentsInfoById?.[currentStepRef.documentId]
      : selectedEngagement?.documentsInfo[EngagementDocumentType.CONSENT_DOCUMENT];

    if (!documentInfo) {
      return of(null);
    }

    const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
    
    const unsignedContactIds = (multiState?.documentContactMappings ?? [])
      .filter(mapping => mapping.engagementDocumentId === documentInfo.documentId && !mapping.signedAt)
      .map(mapping => mapping.id);

    if (unsignedContactIds.length === 0) {
      return of(null);
    }

    const stepType = (currentStepRef?.type as any) || EngagementStepType.CONSENT;

    return this.engagementApiService.confirmConsentMultiContact(unsignedContactIds).pipe(
      tap(() => this.markDocumentContactsAsSigned(getState, setState, unsignedContactIds)),
      switchMap(() => dispatch(new GetEngagementDocument(documentInfo.file.id, stepType))),
      catchError(error => dispatch(new RecoverFromEngagementCompletionFailure(error))),
      take(1),
    );
  }

  /**
   * Marks the given documentContactIds as signed in multipleIdentitySigningState. Without
   * this, the unsignedContactIds filter in the three batch handlers (which excludes
   * mappings with signedAt set) would keep returning the same contacts on every submit,
   * causing already-signed contacts to be re-included in subsequent batch operations.
   */
  private markDocumentContactsAsSigned(
    getState: StateContext<EngagementsStateModel>['getState'],
    setState: StateContext<EngagementsStateModel>['setState'],
    documentContactIds: number[],
  ): void {
    if (!documentContactIds?.length) {
      return;
    }

    const current = getState().multipleIdentitySigningState;

    if (!current) {
      return;
    }

    const idSet = new Set(documentContactIds);
    const signedAt = new Date().toISOString();

    const updatedMappings = current.documentContactMappings
      .map(mapping => idSet.has(mapping.id) ? { ...mapping, signedAt } : mapping);

    setState(patch({
      multipleIdentitySigningState: {
        ...current,
        documentContactMappings: updatedMappings,
      },
    }));
  }

  private static buildDocumentsInfoFromHubSteps(steps: EngagementHubStep[]): {
    documentsInfo: Record<EngagementDocumentType, DocumentInfo>;
    documentsInfoById: Record<number, DocumentInfo & { type: EngagementDocumentType }>;
  } {
    const documentsInfo = {} as Record<EngagementDocumentType, DocumentInfo>;
    const documentsInfoById = {} as Record<number, DocumentInfo & { type: EngagementDocumentType }>;

    for (const step of steps ?? []) {
      if (step.documentId == null) continue;

      const docType = STEP_TYPE_TO_DOCUMENT_TYPE[step.type];
      if (docType == null) continue;

      const info: DocumentInfo = {
        documentId: step.documentId,
        documentContactId: step.documentContactId,
        signatureRequirementType: step.signatureRequirementType ?? SignatureRequirementType.ALL,
        file: { id: step.fileId, fileSource: null, gcsObject: null },
      };

      documentsInfo[docType] = info;
      documentsInfoById[step.documentId] = { ...info, type: docType };
    }

    return { documentsInfo, documentsInfoById };
  }

  /**
   * Maps an EngagementHub REST response to the Engagement shape expected by existing selectors and components.
   */
  /**
   * Re-source the prepayment status (ART-8130 AC3) from the hub-derived engagement onto the
   * EngagementPrepaymentResponse. The Hasura prepayments query exposes none of these fields, so the
   * authoritative status/decline detail live on the hub; this patches them onto the response before it is
   * stored, keeping {@link derivePrepaymentStepState} (and the legacy `active`/`allocationDate` fallback)
   * reading off the prepayment unchanged. Returns the response untouched when there is no prepayment.
   */
  static mergeHubPrepaymentStatus(
    prepayment: EngagementPrepaymentResponse | undefined,
    engagement: Engagement | undefined,
  ): EngagementPrepaymentResponse | undefined {
    if (prepayment == null) {
      return prepayment;
    }

    return {
      ...prepayment,
      prepaymentStatus: engagement?.prepaymentStatus ?? null,
      declineCode: engagement?.prepaymentDeclineCode ?? undefined,
      failureReason: engagement?.prepaymentFailureReason ?? undefined,
      // retryGuidance ships on the hub alongside the decline detail and must cross this merge too,
      // or prepaymentRetryGuidance$ reads undefined on load and the banner falls to @default.
      retryGuidance: engagement?.retryGuidance ?? undefined,
    };
  }

  static mapHubToEngagement(hub: EngagementHub): Engagement {
    const { documentsInfo, documentsInfoById } = EngagementsState.buildDocumentsInfoFromHubSteps(hub.steps);

    // Map hub steps to EngagementStep[] — shapes overlap except requirementType and hub-only fields.
    // When multiple identity signing is active, the hub returns duplicate steps for the same document
    // (one per contact). Deduplicate by merging tasks for steps sharing the same documentId + type.
    const rawSteps: EngagementStep[] = hub.steps?.map(({ documentContactId, signatureRequirementType, fileId, tasks, ...step }) => ({
      ...step,
      tasks: tasks.map(({ signatureRequirementType: _, ...task }) => task as EngagementTask),
    })) ?? [];

    const stepsMap = new Map<string, EngagementStep>();
    for (const step of rawSteps) {
      const stepDocumentId = step.tasks?.find(task => task.documentId)?.documentId;
      const dedupeKey = stepDocumentId != null ? `${step.type}-${stepDocumentId}` : `${step.type}-${stepsMap.size}`;

      if (stepsMap.has(dedupeKey)) {
        const existing = stepsMap.get(dedupeKey);

        if (!existing) {
          continue;
        }

        // Merge tasks and use the "worst" status
        existing.tasks = [...existing.tasks, ...step.tasks];
        existing.hasTasksForCurrentUser = existing.hasTasksForCurrentUser || step.hasTasksForCurrentUser;
        
        if (step.status !== EngagementStepStatus.COMPLETE) {
          existing.status = step.status;
        }
      } else {
        stepsMap.set(dedupeKey, { ...step });
      }
    }
    const steps = [...stepsMap.values()];

    const hasTasksForCurrentUser = steps.some(step => step.hasTasksForCurrentUser);
    const allStepsComplete = steps.every(step => step.status === EngagementStepStatus.COMPLETE);
    const actionRequired = (hub.status === EngagementStatus.SENT && !allStepsComplete && hasTasksForCurrentUser)
      || hub.hasPendingPaymentTaskAfterExecution;

    const report: EngagementReport = {
      steps,
      signatureRequirementsMet: allStepsComplete,
      paymentInfoRequirementMet: allStepsComplete,
      currentUserHasViewedEngagement: hub.currentUserHasViewedEngagement,
      isDocumentViewerOnly: hub.isDocumentViewerOnly,
    };

    // Destructure hub-only fields; spread the rest directly into Engagement
    const { steps: _steps, currentUserHasViewedEngagement: _, isDocumentViewerOnly: __, ...hubFields } = hub;

    return {
      ...hubFields,
      partnerId: 0,
      isExecuted: hub.status === EngagementStatus.EXECUTED,
      shouldShowToClient: true,
      documentsInfo,
      documentsInfoById,
      report,
      actionRequired,
      hasTasksForCurrentUser,
    };
  }

  /**
   * Maps a MinimalEngagementHub (from the list) to the Engagement shape for non-interactive (CANCELED/UNDER_REVISION) engagements.
   * Skips steps, documents, and report since the view container hides all interactive content for these statuses.
   */
  static mapMinimalHubToEngagement(hub: MinimalEngagementHub): Engagement {
    return {
      id: hub.id,
      name: '',
      status: hub.status,
      clientName: '',
      partnerName: hub.partnerName,
      startDate: hub.startDate,
      partnerId: 0,
      mode: EngagementMode.AUTOMATIC,
      isReadonly: true,
      isExecuted: false,
      isStartOnAcceptance: false,
      shouldShowToClient: true,
      actionRequired: false,
      enableAutomaticPayments: false,
      hasJobs: false,
      paymentInfoType: PaymentInfoType.NONE,
      primaryContactId: 0,
      documentsInfo: {} as Engagement['documentsInfo'],
    };
  }

  static isAutoDraftEngagement(engagement: Engagement): boolean {
    return engagement.enableAutomaticPayments && engagement.hasJobs;
  }
}
